/**
 * clockSync.ts
 *
 * NTP-style clock synchronisation over Socket.IO — and, since the server-wide
 * demo mode (card D1, plan §3.5), the demo clock's **rate-aware frame**.
 *
 * Measures the offset between the client's Date.now() and the server's
 * Date.now() by sending N ping/pong round-trips and taking the median.
 *
 * Usage:
 *   await syncClock(socket);   // run once per connection
 *   const t = serverNow();     // anywhere — hub time, or DEMO time in a demo
 *   serverRate();              // 1 normally, 288× while the demo plays
 *
 * A normal hub answers `clock:ping` with `{ serverTime }` and `serverNow()` is
 * that clock through the measured offset — exactly the behaviour this file has
 * always had. A demo hub adds `demo`, an **affine frame**:
 *
 *   { simMs, atRealMs, rate, playing }
 *
 * i.e. "sim time was `simMs` when the hub's real clock read `atRealMs`, and it
 * advances `rate` sim-ms per real-ms". `serverNow()` then answers sim time, so
 * every clock surface in the app — the dashboard clock widgets (through
 * `useServerClock`), the BIM sun (`syncSunClock`), zone audio — follows the demo
 * with no call-site change, and a paused demo freezes them (returns `simMs`)
 * instead of drifting.
 *
 * Nothing here polls: the frame arrives on the `clock:ping` ack, on the socket's
 * own `demo:clock`/`demo:state` events (`applyDemoClockEvent` /
 * `applyDemoStatusEvent`, wired by `composables/useDemoClock.ts`) and once per
 * (re)connect through `refreshDemoFrame`.
 */

const PING_COUNT = 8;

/**
 * One ping re-anchors a demo frame; the NTP offset stays `syncClock`'s job.
 * Bounded, because an ack the hub never answers would otherwise leave the
 * promise (and its caller) pending forever.
 */
const FRAME_PING_TIMEOUT_MS = 5_000;

let clockOffset = 0; // serverTime = Date.now() + clockOffset
let synced = false;

/**
 * A demo-clock frame as the hub publishes it (D0's `DemoClockFrame`), anchored
 * on the HUB's real clock — never on this device's.
 */
export interface DemoClockFrame {
  /** The simulated instant (epoch ms). */
  simMs: number;
  /** The hub's real wall clock when `simMs` was true (epoch ms). */
  atRealMs: number;
  /** Sim-ms per real-ms: 1 outside a demo, 288 in it by default. */
  rate: number;
  /** Whether sim time advances at all; paused freezes `simMs`. */
  playing: boolean;
}

/**
 * The day the demo clock is on. The `demo:clock` and `demo:state` payloads carry
 * it; a `clock:ping` ack's frame does not, so the day is whatever the last
 * broadcast said (D3 binds it to a script).
 */
export interface DemoClockDay {
  /** 0-based index of the script day (`day 1` in the plans and the player). */
  dayIndex: number;
  dayId: string | null;
  season: string | null;
  /** The script's own label for the day, when the hub sends one (D3). */
  label?: string | null;
}

let demoFrame: DemoClockFrame | null = null;
let demoDay: DemoClockDay | null = null;

/**
 * Change feed for reactive consumers (`composables/useDemoClock.ts`). A plain
 * callback set, so this module stays framework-free and importable by a node
 * test — and so the app needs no watcher, no store and no Vue to read the clock.
 */
const frameWatchers = new Set<(frame: DemoClockFrame | null) => void>();

/**
 * The socket this needs: socket.io's acknowledgement round-trip, structurally.
 * Typed here rather than imported so the helper stays usable with the socket
 * store's own union (`Socket | WebRTCStatusBridge`, see stores/socket.ts).
 */
interface AckSocket {
  emit(event: string, payload: unknown, ack: (response: ClockPingAck) => void): unknown;
}

/** `clock:ping`'s answer (D0): the REAL hub time, plus the demo frame when one is on. */
interface ClockPingAck {
  serverTime: number;
  demo?: DemoClockFrame;
}

/** The `demo:clock` payload (D0). No `atRealMs`: a frame is true when it is sent. */
interface DemoClockBroadcast {
  simMs: number;
  iso?: string;
  dayId: string | null;
  dayIndex: number;
  season: string | null;
  rate: number;
  playing: boolean;
  /** Forward compatibility with D3's script day label. */
  label?: string | null;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** A frame off the wire, or `null` when the payload is not one. */
function readFrame(value: unknown): DemoClockFrame | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Partial<DemoClockFrame>;
  if (!isFiniteNumber(raw.simMs) || !isFiniteNumber(raw.atRealMs)) return null;
  if (!isFiniteNumber(raw.rate) || raw.rate <= 0) return null;
  return {
    simMs: raw.simMs,
    atRealMs: raw.atRealMs,
    rate: raw.rate,
    playing: raw.playing === true,
  };
}

/** The day block off the wire, or `null` when there is none to read. */
function readDay(value: unknown): DemoClockDay | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  return {
    dayIndex: isFiniteNumber(raw.dayIndex) ? Math.max(0, Math.trunc(raw.dayIndex)) : 0,
    dayId: typeof raw.dayId === 'string' ? raw.dayId : null,
    season: typeof raw.season === 'string' ? raw.season : null,
    label: typeof raw.label === 'string' ? raw.label : null,
  };
}

function notifyFrameWatchers(): void {
  for (const watcher of frameWatchers) {
    try {
      watcher(demoFrame);
    } catch {
      // A consumer that throws must never be able to break the clock.
    }
  }
}

/**
 * Where a frame says the sim clock is at `realNowMs` — the hub's own
 * `interpolate()` (`otto/src/services/demo/DemoClock.ts`) mirrored, with one
 * client-only step in front: `atRealMs` is on the HUB's real clock, so this
 * device's clock is first moved onto that timeline through the measured offset.
 * Uncorrected, a device whose clock is two minutes off would show sim time two
 * minutes × rate away — ten sim hours at 288×.
 *
 * With no measured offset (a client that has not run `syncClock` yet, and
 * `scripts/verify-demo-clock-frame.mjs`, which measures nothing) this is exactly
 * `simMs + (Date.now() − atRealMs) × rate`.
 */
export function demoFrameInstant(frame: DemoClockFrame, realNowMs: number, offsetMs = 0): number {
  if (!frame.playing) return frame.simMs;
  const elapsed = Math.max(0, realNowMs + offsetMs - frame.atRealMs);
  return frame.simMs + elapsed * frame.rate;
}

/**
 * Run the NTP estimation. Resolves when complete.
 * Safe to call multiple times (e.g. on reconnect).
 *
 * The demo frame rides along on the acks: when the hub is a demo, the last
 * answer's frame becomes the client's frame (so sim time is live from the first
 * sync, even while the demo is paused and no `demo:clock` broadcast flows); when
 * none of the acks carries one, the hub is not a demo and any frame we still
 * hold is dropped — `rate` goes back to 1 and `serverNow()` back to hub time.
 */
export async function syncClock(socket: AckSocket): Promise<void> {
  const samples: number[] = [];
  let demo: DemoClockFrame | null = null;

  for (let i = 0; i < PING_COUNT; i++) {
    const t0 = Date.now();
    const answer = await new Promise<ClockPingAck | null>((resolve) => {
      socket.emit('clock:ping', { t0 }, (res: ClockPingAck) => {
        resolve(res ?? null);
      });
    });
    const t1 = Date.now();
    if (!answer || !isFiniteNumber(answer.serverTime)) continue;
    const rtt = t1 - t0;
    // NTP formula: offset = serverTime - (t0 + rtt/2)
    samples.push(answer.serverTime - (t0 + rtt / 2));
    const frame = readFrame(answer.demo);
    if (frame) demo = frame;
  }

  if (samples.length === 0) return;

  // Median to discard outliers
  samples.sort((a, b) => a - b);
  clockOffset = samples[Math.floor(samples.length / 2)];
  synced = true;

  if (demo) {
    applyDemoFrame(demo);
  } else {
    resetDemoFrame();
  }

  console.log(
    `[ClockSync] offset=${clockOffset.toFixed(1)} ms` +
      (demo ? ` demo rate=${demo.rate}${demo.playing ? '' : ' paused'}` : ''),
  );
}

/**
 * One `clock:ping`, kept solely to re-anchor the demo frame — the NTP offset is
 * `syncClock`'s job and this never touches it.
 *
 * This is what a (re)connect needs: `demo:clock` only flows while the demo is
 * PLAYING, so a client that reconnects into a paused demo would otherwise sit on
 * real time until someone opened a clock surface. Resolves `true` when a demo
 * frame was applied, `false` when the hub answered without one (or not at all).
 */
export async function refreshDemoFrame(socket: AckSocket): Promise<boolean> {
  const answer = await new Promise<ClockPingAck | null>((resolve) => {
    const timer = setTimeout(() => resolve(null), FRAME_PING_TIMEOUT_MS);
    try {
      socket.emit('clock:ping', { t0: Date.now() }, (res: ClockPingAck) => {
        clearTimeout(timer);
        resolve(res ?? null);
      });
    } catch {
      clearTimeout(timer);
      resolve(null);
    }
  });

  if (!answer) return false;

  const frame = readFrame(answer.demo);
  if (frame) {
    applyDemoFrame(frame);
    return true;
  }
  resetDemoFrame();
  return false;
}

/**
 * Returns the estimated current hub time in ms (Date.now() equivalent) — which
 * is DEMO time whenever a demo frame is set (plan §3.5: `serverNow()` is demo
 * time everywhere), and the plain hub clock otherwise.
 */
export function serverNow(): number {
  const frame = demoFrame;
  if (frame) return demoFrameInstant(frame, Date.now(), clockOffset);
  return realNow();
}

/**
 * The hub's REAL wall clock — offset-corrected, and never demo time.
 *
 * Anything the hub itself stamps in real ms has to be compared against this, not
 * `serverNow()`: the audio zones' PCM chunks carry `playAtMs` from the server's
 * own `Date.now()` (`services/zoneAudioPlayback.ts`), and in a demo `serverNow()`
 * is months away, so every chunk would be dropped as "impossibly far ahead".
 * Demo time is what a clock SURFACE shows; this is what a transport measures.
 */
export function realNow(): number {
  return Date.now() + clockOffset;
}

/** Sim-ms per real-ms: 1 outside a demo, 288× (or whatever the hub runs at) in one. */
export function serverRate(): number {
  return demoFrame?.rate ?? 1;
}

/** The frame this client is following, or `null` when the hub is not a demo. */
export function serverFrame(): DemoClockFrame | null {
  return demoFrame;
}

/** The demo day the last broadcast named, or `null` when nothing has said. */
export function serverDemoDay(): DemoClockDay | null {
  return demoDay;
}

/**
 * Adopt a frame (exactly as the hub published it — hub-real anchoring).
 * `day` is optional because an ack's frame carries no day: omitted leaves the
 * day as it stands, `null` clears it.
 */
export function applyDemoFrame(frame: DemoClockFrame, day?: DemoClockDay | null): void {
  demoFrame = { ...frame };
  if (day !== undefined) demoDay = day === null ? null : { ...day };
  notifyFrameWatchers();
}

/**
 * `demo:clock` — the 1 Hz frame while the demo plays. It carries no `atRealMs`
 * ("true when sent"), so the receipt instant is the anchor, on the HUB's
 * timeline. Anchoring on receipt can only make the client lag by the one-way
 * latency; it can never run ahead of the hub.
 */
export function applyDemoClockEvent(payload: unknown): void {
  const raw = (payload ?? {}) as Partial<DemoClockBroadcast>;
  if (!isFiniteNumber(raw.simMs) || !isFiniteNumber(raw.rate) || raw.rate <= 0) return;
  applyDemoFrame(
    {
      simMs: raw.simMs,
      atRealMs: Date.now() + clockOffset,
      rate: raw.rate,
      playing: raw.playing === true,
    },
    readDay(raw) ?? undefined,
  );
}

/**
 * `demo:state` — demo mode, script or playback changed (a rate change or a seek
 * is exactly when a frame that was only extrapolated must be re-anchored, or a
 * clock would lag or snap). Demo mode going off clears the frame, so every clock
 * drops back to hub time.
 */
export function applyDemoStatusEvent(payload: unknown): void {
  if (!payload || typeof payload !== 'object') return;
  const raw = payload as { mode?: { enabled?: unknown }; clock?: unknown; frame?: unknown };
  if (raw.mode && typeof raw.mode === 'object' && raw.mode.enabled === false) {
    resetDemoFrame();
    return;
  }
  const frame = readFrame(raw.frame);
  if (!frame) return;
  applyDemoFrame(frame, readDay(raw.clock) ?? undefined);
}

/** Back to hub time (demo mode off, or a hub switch). */
export function resetDemoFrame(): void {
  if (demoFrame === null && demoDay === null) return;
  demoFrame = null;
  demoDay = null;
  notifyFrameWatchers();
}

/**
 * Forget everything this module holds: offset, sync flag and demo frame. Called
 * on a deliberate disconnect (the next hub must not inherit this one's clock)
 * and by the unit test, which needs a known state per case.
 */
export function resetClockSync(): void {
  clockOffset = 0;
  synced = false;
  demoFrame = null;
  demoDay = null;
  notifyFrameWatchers();
}

/**
 * Watch the frame. The listener runs with the new value on every applied or
 * cleared frame; the returned function unsubscribes.
 */
export function onDemoFrameChange(listener: (frame: DemoClockFrame | null) => void): () => void {
  frameWatchers.add(listener);
  return () => {
    frameWatchers.delete(listener);
  };
}

export function isSynced(): boolean {
  return synced;
}
